import React, { useEffect, useState, useCallback } from 'react';

function PmsExpenseApproval({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const loadMasters = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const [c, d] = await Promise.all([
        fetch('/api/v1/expense/categories', { headers: headers() }),
        fetch('/api/v1/expense/departments', { headers: headers() })
      ]);
      const [cd, dd] = await Promise.all([c.json(), d.json()]);
      setCategories(cd.rows || cd || []);
      setDepartments(dd.rows || dd || []);
    } catch { /* ignore — masters are decoration here, not blocking */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const loadData = useCallback(async () => {
    if (!selectedProperty?.id) { setRows([]); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/v1/expense/?status=SUBMITTED&limit=500', { headers: headers() });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to load'); return; }
      setRows(data.rows || []);
      setError('');
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  useEffect(() => { loadMasters(); loadData(); }, [loadMasters, loadData]);

  const loadDetail = async (id) => {
    try {
      const res = await fetch(`/api/v1/expense/${id}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load expense');
      setSelected(data);
    } catch { setError('Network error'); }
  };

  const approve = async (id) => {
    if (!window.confirm('Approve this expense? It will be posted to the accounting ledger.')) return;
    setBusyId(id);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/expense/${id}/approve`, {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ remark: '' })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to approve');
        return;
      }
      if (data.journal_error) {
        setMessage(`Approved but journal posting failed: ${data.journal_error}. Will retry.`);
      } else {
        setMessage(`Expense approved and posted (status: ${data.status}).`);
      }
      setSelected(null);
      loadData();
    } catch {
      setError('Network error');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    if (!rejectId) return;
    if (!rejectReason || rejectReason.trim().length < 5) return setError('Reject reason must be at least 5 characters');
    setBusyId(rejectId);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/expense/${rejectId}/reject`, {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ reason: rejectReason })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to reject');
        return;
      }
      setMessage('Expense rejected.');
      setRejectId(null);
      setRejectReason('');
      setSelected(null);
      loadData();
    } catch {
      setError('Network error');
    } finally {
      setBusyId(null);
    }
  };

  const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const total = rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Approval Queue</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{error}</div>}
      {message && <div style={{ background: '#dcfce7', color: '#166534', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{message}</div>}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '0.7rem 1rem', borderBottom: '1px solid #e5e7eb', background: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600 }}>{rows.length} pending — Total {fmtMoney(total)}</span>
          <button onClick={loadData} disabled={loading} style={{ padding: '0.4rem 0.8rem', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                <th style={{ padding: '0.55rem' }}>Expense No</th>
                <th style={{ padding: '0.55rem' }}>Date</th>
                <th style={{ padding: '0.55rem' }}>Category</th>
                <th style={{ padding: '0.55rem' }}>Department</th>
                <th style={{ padding: '0.55rem' }}>Payee</th>
                <th style={{ padding: '0.55rem' }}>Mode</th>
                <th style={{ padding: '0.55rem', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '0.55rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>No expenses pending approval.</td></tr>
              ) : rows.map(r => {
                const catName = categories.find(c => c.category_id === r.category_id)?.category_name || '—';
                const deptName = departments.find(d => d.department_id === r.department_id)?.dept_name || '—';
                return (
                <tr key={r.expense_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{r.expense_no}</td>
                  <td style={{ padding: '0.5rem' }}>{r.expense_date}</td>
                  <td style={{ padding: '0.5rem' }}>{catName}</td>
                  <td style={{ padding: '0.5rem' }}>{deptName}</td>
                  <td style={{ padding: '0.5rem' }}>{r.payee_name || '—'}</td>
                  <td style={{ padding: '0.5rem' }}>{r.payment_mode}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmtMoney(r.total_amount)}</td>
                  <td style={{ padding: '0.5rem', display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                    <button onClick={() => loadDetail(r.expense_id)} style={{ padding: '0.3rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: '0.78rem' }}>View</button>
                    <button onClick={() => approve(r.expense_id)} disabled={busyId === r.expense_id} style={{ padding: '0.3rem 0.6rem', border: 'none', borderRadius: 4, background: '#10b981', color: '#fff', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                      {busyId === r.expense_id ? '…' : 'Approve'}
                    </button>
                    <button onClick={() => { setRejectId(r.expense_id); setRejectReason(''); }} disabled={busyId === r.expense_id} style={{ padding: '0.3rem 0.6rem', border: 'none', borderRadius: 4, background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                      Reject
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div onClick={() => setSelected(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: '1.2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
              <h3 style={{ margin: 0 }}>Expense {selected.header?.expense_no}</h3>
              <button onClick={() => setSelected(null)} style={{ border: 'none', background: 'transparent', fontSize: '1.4rem', cursor: 'pointer' }}>×</button>
            </div>
            {selected.header && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.5rem', fontSize: '0.85rem' }}>
                <div><strong>Date:</strong> {selected.header.expense_date}</div>
                <div><strong>Status:</strong> {selected.header.status}</div>
                <div><strong>Payee:</strong> {selected.header.payee_name || '—'}</div>
                <div><strong>Mode:</strong> {selected.header.payment_mode}</div>
                <div><strong>Invoice:</strong> {selected.header.invoice_no || '—'}</div>
                <div><strong>Subtotal:</strong> {fmtMoney(selected.header.subtotal)}</div>
                <div><strong>Tax:</strong> {fmtMoney(Number(selected.header.cgst_amount || 0) + Number(selected.header.sgst_amount || 0) + Number(selected.header.igst_amount || 0))}</div>
                <div><strong>TDS:</strong> {fmtMoney(selected.header.tds_amount)}</div>
                <div style={{ gridColumn: '1 / -1' }}><strong>Total:</strong> <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{fmtMoney(selected.header.total_amount)}</span></div>
                <div style={{ gridColumn: '1 / -1' }}><strong>Description:</strong> {selected.header.description || '—'}</div>
              </div>
            )}
            <div style={{ marginTop: '1.2rem', display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setSelected(null)} style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, background: '#f9fafb', cursor: 'pointer' }}>Close</button>
              <button onClick={() => approve(selected.header.expense_id)} disabled={busyId === selected.header.expense_id} style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: 6, background: '#10b981', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Approve & Post</button>
              <button onClick={() => { setRejectId(selected.header.expense_id); setRejectReason(''); }} disabled={busyId === selected.header.expense_id} style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: 6, background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Reject</button>
            </div>
          </div>
        </div>
      )}

      {rejectId && (
        <div onClick={() => setRejectId(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, maxWidth: 480, width: '100%', padding: '1.2rem' }}>
            <h3 style={{ marginTop: 0 }}>Reject Expense</h3>
            <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Provide a reason (minimum 5 characters). The submitter will see this in the audit trail.</p>
            <textarea
              rows={4}
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setRejectId(null)} style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, background: '#f9fafb', cursor: 'pointer' }}>Cancel</button>
              <button onClick={reject} disabled={busyId === rejectId || rejectReason.trim().length < 5} style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: 6, background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 600, opacity: rejectReason.trim().length < 5 ? 0.5 : 1 }}>
                {busyId === rejectId ? 'Rejecting…' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PmsExpenseApproval;
